import * as React from "react";
import { useState } from "react";
import { ToolStep } from "../../../types";
import { join } from "../../../utils/classNames";
import { CheckCircle, ChevronDown, Ring, Spinner, XCircle } from "../../icons";
import styles from "./AgentTrace.module.css";

interface Props {
	steps: ToolStep[];
}

/** `gw-abc_clients___get_client_info` → "Get Client Info": the gateway/target prefix is noise. */
export const toolLabel = (name: string): string =>
	(name.split("___").pop() ?? name)
		.replace(/[_-]+/g, " ")
		.trim()
		.replace(/\b\w/g, (c) => c.toUpperCase());

const formatDuration = (ms: number): string => `${(ms / 1000).toFixed(1)}s`;

const statusIcon = (status: ToolStep["status"]): React.ReactElement => {
	switch (status) {
		case "running":
			return <Spinner className={styles.spinner} />;
		case "success":
			return <CheckCircle />;
		case "error":
			return <XCircle />;
		case "cancelled":
			return <Ring />;
	}
};

export const AgentTrace: React.FC<Props> = ({ steps }) => {
	const [open, setOpen] = useState(true);

	return (
		<div className={styles.base}>
			<button
				type={"button"}
				className={join(styles.header, !open && styles.closed)}
				onClick={() => setOpen((o) => !o)}
				aria-expanded={open}
			>
				<ChevronDown />
				<span>{"Agent trace"}</span>
				<span>{`(${steps.length} ${steps.length === 1 ? "step" : "steps"})`}</span>
			</button>
			{open && (
				<ol className={styles.steps}>
					{steps.map((step) => (
						<li key={step.id} className={styles.step}>
							<span className={join(styles.icon, styles[step.status])}>{statusIcon(step.status)}</span>
							<span className={styles.label}>{toolLabel(step.name)}</span>
							{step.status !== "running" && step.durationMs !== undefined && (
								<span className={styles.duration}>{formatDuration(step.durationMs)}</span>
							)}
						</li>
					))}
				</ol>
			)}
		</div>
	);
};
